import { Armchair, Search, CalendarDays, CircleX, CloudDownload, Loader, Copy, RefreshCw, FolderOutput, MonitorCheck } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from "../DataTablePagination";
import Spacing from "../Spacing";
import DatePicker from "react-datepicker";
import WaitingRoomDataTable from "../DataTable/WaitingRoomDataTable";
import { formatDate, generateWaitingRoomData,  numberWithCommas, exchangeDate, debounce, getSpeciality, userMessageTemplates, usPhoneFormat, can_visit, userAccessToken, role_has, getSecureData, setSecureData, getDateString } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { checkBulkClaimsData, checkBulkEligibilityData, copyAllPrevisitCodes, deleteBulkWaitingData, deleteWaitingRoomData, exportAllWaitingRoom, fetchNewVeradigmData, fetchWaitingRoom } from "../../services/waitingRoom.js";
import { sendReviewRequest } from "../../services/reviews.js";
import { notify, getPractice } from "../../helpers";
import { useNavigate } from "react-router-dom";
import BulkDeleteModal from "../Modal/BulkDeleteModal.jsx";
import { apiServicesUrl } from "../../services/baseUrl.js";
import ClaimCheckImportModal from "../Modal/ClaimCheckImportModal.jsx";

export default function WaitingRoom({ selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit, setLetterLimit, changePracticeSpeciality, downloadAllPrevisit, previsitButtonLoader, waitingRoomProviders, showPrevisitLoaderData }) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  let specilityId = getSpeciality()?.id || 32;
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    specilityId = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    specilityId = changePracticeSpeciality?.id || 32;
  }
  let perPage = 10;
  if(can_visit('ehr_veradigm')) { specilityId = 32; perPage = 30; }
  let dateStr = getDateString();

  const navigate = useNavigate();
  const [messageTemplate] = useState(userMessageTemplates());
  const [buttonLoader, setButtonLoader] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [waitingRoomData, setWaitingRoomData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [dateRange, setDateRange] = useState([dateStr, dateStr]);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: perPage || 10,
    search_term: "",
    start_date: dateStr,
    end_date: dateStr,
    account_id: account_id,
    location_id: location_id,
    speciality_id: specilityId,
    order_by: [],
    provider_id: !role_has("Admin") ? user_id : null
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [waitingRoomBulkDelete, setWaitingRoomBulkDelete] = useState(false);
  const [copyAllLoader, setCopyAllLoader] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [activeVeradigmFetchData, setActiveVeradigmFetchData] = useState(false);
  const userProviders = waitingRoomProviders;
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [exportAllButtonLoader, setExportAllButtonLoader] = useState(false);
  const [eligibilityCheckLoader, setEligibilityCheckLoader] = useState(false);
  const [claimsCheckLoader, setClaimsCheckLoader] = useState(false);
  const [bulkClaimsCheckModalOpen, setBulkClaimsCheckModalOpen] = useState(false);
  const [bulkClaimsCheckLoader, setBulkClaimsCheckLoader] = useState(false);

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  useEffect(() => {
    const provider = userProviders?.find((p) => p?.provider_id == user_id);
    if(provider) {
      setSelectedProvider(`${provider?.last_name} ${provider?.first_name}` || null);
    }
  }, [userProviders, user_id]);

  const handleSignOutSheet = (patientId, waitingId, serviceDate) => {
    // navigate(`/signout-sheet`, {
    //   state: {
    //     patient_id: patientId,
    //     waiting_id: waitingId
    //   },
    // });

    const url = `/previsit?waiting_id=${waitingId}&type=print`;
    window.open(url, '_blank');
  };

  const downloadSignOutSheet = (patientId, waitingId, serviceDate) => {
    const url = `/previsit?waiting_id=${waitingId}&type=download`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    loadWaitingRoomData(filter);
  }, [filter]);

  useEffect(() => {
    const checkAndFetchVeradigmData = async () => {
      const today = new Date().toISOString().slice(0, 10);
      const lastFetchedDate = await getSecureData("_veradigm_fetched_date");
      if (can_visit('ehr_veradigm') && lastFetchedDate !== today && waitingRoomData.length === 0 && !role_has("Admin")) {
        // notify("success", "Sent a request to sync your Veradigm data", 2);
        await setSecureData("_veradigm_fetched_date", today);
        loadVeradigmData();
      }
    };

    checkAndFetchVeradigmData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, waitingRoomData]);

  // Loads the waiting room data based on the provided filter.
  const loadWaitingRoomData = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchWaitingRoom(filter);
      const generateData = generateWaitingRoomData(response?.data?.results);
      setWaitingRoomData(generateData || []);
      setPaginationCount({
        totalCount: response?.data?.count,
        totalPages: response?.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadWaitingRoomData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  // Handles the change of rows per page in the filter.
  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  // Handles the page change event.
  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  // Handles the change event for the search term input field.
  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  // Handle date change (supports both range array and single date)
  const handleDateChange = (update) => {
    let start = null;
    let end = null;

    if (Array.isArray(update)) {
      [start, end] = update;
    } else {
      start = update;
      end = update;
    }

    if (start && end && formatDate(start) === formatDate(end)) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
        page: 1,
      });
    } else if (start && end) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(end),
        page: 1,
      });
    } else if (start) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
        page: 1,
      });
    }
    setDataLoading(true);

    setDateRange([start, end || start]);
  };

  // Handles the checkbox change event for a specific waitingRoomData.
  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setWaitingRoomData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
      } else {
        updatedCheckedIds.add(id);
      }
      return updatedCheckedIds;
    });
  };

  // Handles the select all functionality for waitingRoomData.
  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
    }

    setSelectAll(!selectAll);

    setWaitingRoomData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  // Deletes an waitingRoomData record.
  const handleDeleteRecord = async (id) => {
    try {
      setDataLoading(true);
      await deleteWaitingRoomData(id, account_id, location_id);
      notify("success", "Record deleted successfully.", 0.5);
      await loadWaitingRoomData(filter);
    } catch (error) {
      setDataLoading(false);
      // console.log("🚀 ~ handleDeleteRecord ~ error:", error);
    }
  };

  // Handles sending a review request.
  const handleSendReviewRequest = async (data) => {
    try {
      await sendReviewRequest(data);
      notify("success", "Review request sent successfully.", 0.5);
    } catch (error) {
      // console.log("🚀 ~ handleSendReviewRequest ~ error:", error);
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === specilityId;

    if (sameLocation && sameAccount && sameSpec) return;
    
    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: specilityId,
      page: 1,
    });
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specilityId]);

  const channel = new BroadcastChannel('tab_practice_change');
  useEffect(() => {
    channel.onmessage = (event) => {
      setFilter({
        ...filter,
        location_id: event?.data?.location_id,
        account_id: event?.data?.account_id,
        page: 1,
      });
      location_id = event?.data?.location_id;
      account_id = event?.data?.account_id;
      setCheckedIds([]);
    };
    return () => {
      channel.close();
    };
  }, [channel, location_id, account_id]);

  // const handleDownloadSelect = async (e) => {
  //   setButtonLoader(true);
  //   try {
  //     const ids = checkedIds;

  //     ids.forEach(id => {
  //       if (!isNaN(id) && id !== null && id !== undefined) {

  //         const matchingWaiting = waitingRoomData.find((item) => item.id === id);
  //         if (matchingWaiting) {
  //           const url = `/previsit?waiting_id=${id}&type=download`;
  //           // window.open(url, '_blank');
  //           window.open(url, id, 'height=100,width=100,left=1000,top=1000');
  //         }
  
  //       } else {
  //         console.error('Invalid ID encountered:', id);
  //       }
  //     });
      
  //   } catch (error) {
  //     console.log("🚀 ~ DownloadSelect ~ error:", error);
  //   } finally {
  //     setTimeout(() => {
  //       setButtonLoader(false);
  //     }, 7000);
  //   }
  // };
  
  const handleDownloadSelect = async (e) => {
    setButtonLoader(true);

    try {
      const ids = checkedIds;

      for (const id of ids) {
        if (!isNaN(id) && id !== null && id !== undefined) {
          const matchingWaiting = waitingRoomData.find(
            (item) => item.id === id
          );

          if (matchingWaiting) {
            let url = `${apiServicesUrl}/waiting_rooms/previsit_download?account_id=${account_id}&location_id=${location_id}&waiting_room_id=${id}`;

            const fetchPdfWithRetry = async (
              url,
              retryLastRead = 0,
              retryCount = 0
            ) => {
              if (retryCount >= 10) {
                throw new Error("Max retry attempts reached.");
              }

              let fetchUrl = url;
              if (retryLastRead) {
                const separator = url.includes("?") ? "&" : "?";
                fetchUrl = `${url}${separator}last_read=${retryLastRead}`;
              }

              const response = await fetch(fetchUrl, {
                method: "GET",
                headers: {
                  Accept: "application/pdf, application/json",
                  Authorization: `Bearer ${userAccessToken()}`,
                },
              });

              if (response.status === 202) {
                const json = await response.json();
                console.log(
                  `Waiting for report to be ready for waiting_room_id=${id}, retry #${
                    retryCount + 1
                  }`,
                  json.detail
                );

                await new Promise((res) => setTimeout(res, 5000));

                return fetchPdfWithRetry(
                  url,
                  json.detail.last_read,
                  retryCount + 1
                );
              }

              const contentType = response.headers.get("content-type") || "";

              if (!response.ok) {
                if (contentType.includes("application/json")) {
                  const errorData = await response.json();
                  throw new Error(
                    errorData.detail || "Failed to download the report."
                  );
                } else {
                  throw new Error("Server returned an unexpected response.");
                }
              }

              if (!contentType.includes("application/pdf")) {
                throw new Error(
                  "Invalid response format. Expected a PDF file."
                );
              }

              const blob = await response.blob();
              return { blob, headers: response.headers };
            };

            const { blob, headers } = await fetchPdfWithRetry(url);
            // const contentDisposition = headers.get("content-disposition");
            let fileName = `${matchingWaiting.dos}-${matchingWaiting.firstName}-${matchingWaiting.lastName}.pdf`;
            // if (contentDisposition) {
            //   const match = contentDisposition.match(/filename="?([^"]+)"?/);
            //   if (match && match[1]) {
            //     fileName = decodeURIComponent(match[1]);
            //   }
            // }

            const fileURL = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = fileURL;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(fileURL);
          }
        } else {
          console.error("Invalid ID encountered:", id);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ DownloadSelect ~ error:", error);
      notify("error", "Something went wrong!");
    } finally {
      setButtonLoader(false);
    }
  };
  
  const handleReferralNavigation = (data) => {
    navigate(`/new-referral`, { state: { data } });
  };

  const filterByProvider = async (e) => {
    const selectedId = e.target.value;
    if(selectedId) {
      const selected = userProviders?.find((p) => p?.provider_id == selectedId);
      setSelectedProvider(`${selected?.last_name} ${selected?.first_name}` || null);
    } else {
      setSelectedProvider(null);
    }
    setFilter({ ...filter, provider_id: selectedId || null, page: 1 });
    setDataLoading(true);
  };

  const copyCodesData = async (filterData, waitingId=null) => {
    if (!waitingId) {
      setCopyAllLoader(true);
    }
    try {
      const response = await copyAllPrevisitCodes(filterData, waitingId);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.length === 0) {
            notify("error", "No codes found.");
          } else {
            const formattedText = response?.data?.map(patient => {
              const cptCodes = patient?.cpt_codes.join(', ');
              const icdCodes = patient?.icd_codes.join(', ');
    
              return `Name: ${patient?.patient_name}
Phone: ${usPhoneFormat(patient?.phone)}
DOB: ${exchangeDate(patient?.dob)}
CPT: ${cptCodes}
ICD: ${icdCodes}`;
            }).join('\n\n');

            await navigator?.clipboard?.writeText(formattedText);
            notify("success", "Codes copied successfully!");
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 0.5);
      }
    } finally {
      setCopyAllLoader(false);
    }
  }

  const handleBulkDeleteRecord = async (e) => {
    setDataLoading(true);
    try {
      const deleteData = {
        account_id: account_id,
        location_id: location_id,
        selected_id: checkedIds,
      };

      const response = await deleteBulkWaitingData(deleteData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.success){
            notify("success", response?.data?.success, 0.5);
            setGlobalCheckedIds(new Set());
            setCheckedIds([]);
            await loadWaitingRoomData(filter);
          } else {
            notify("error", response?.data?.error);
          }
        }
      }
    } catch (error) {
      // console.log("🚀 ~ handleBulkDeleteRecord ~ error:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const loadVeradigmData = async () => {
    const { start_date, end_date } = filter;

    if (!start_date || !end_date) {
      notify('info', 'Please select a date first', 2);
      return;
    }

    if (start_date !== end_date) {
      notify('info', 'Please select a single date to fetch data', 2);
      return;
    }
    let provider = null;
    if (filter.provider_id) {
      provider = userProviders?.find((p) => p?.provider_id == filter?.provider_id);
    } else if (role_has("Admin") && filter?.provider_id === null) {
      provider = { ehr_username: "All", provider_id: null };
    } else {
      provider = userProviders?.find((p) => p?.provider_id == user_id);
    }

    const veradigmPostData = {
      account_id,
      location_id,
      start_date,
      provider_id: provider?.provider_id || null,
      ehr_username: provider?.ehr_username || null,
    }
    
    try {
      setActiveVeradigmFetchData(true);
      setDataLoading(true);
      const response = await fetchNewVeradigmData(veradigmPostData);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail, 2);
        } else {
          if(response?.data?.status === "error") {
            notify("error", response?.data?.message, 2);
          } else {
            await loadWaitingRoomData({...filter, page:1});
          }
        }
      }
    } catch (error) {
      notify("error", "An error occurred while fetching Veradigm data.", 2);
    } finally {
      setActiveVeradigmFetchData(false);
      setDataLoading(false);
    }
  }

  const handleExportAll = async (e) => {
    setExportAllButtonLoader(true);
    try {
      const response = await exportAllWaitingRoom(filter);

      if (response.detail) {
        notify("error", response.detail, 5);
      }
      if (response && response.data) {
        const responseData = response.data;
        const dataBlob = new Blob([responseData], {
          type: "text/csv;charset=utf-8;",
        });
        const generateURL = window.URL.createObjectURL(dataBlob);

        // File Name Generate
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const filename = `Waiting_list_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.csv`;

        const link = document.createElement("a");
        link.href = generateURL;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        window.URL.revokeObjectURL(generateURL);
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail);
      }
    } finally {
      setExportAllButtonLoader(false);
    }
  };

  const checkBulkEligibility = async (e) => {
    setEligibilityCheckLoader(true);
    try {
      const eligibilityData = {
        account_id: account_id,
        location_id: location_id,
        waiting_room_ids: checkedIds,
      };

      const response = await checkBulkEligibilityData(eligibilityData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.status === "success"){
            notify("success", response?.data?.message);
            await loadWaitingRoomData(filter);
          } else {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail);
      }
    } finally {
      setEligibilityCheckLoader(false);
    }
  };

  const checkBulkClaims = async (e) => {
    setClaimsCheckLoader(true);
    try {
      const claimsData = {
        account_id: account_id,
        location_id: location_id,
        waiting_room_ids: checkedIds,
      };

      const response = await checkBulkClaimsData(claimsData);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.status === "success"){
            notify("success", response?.data?.message);
            await loadWaitingRoomData(filter);
          } else {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail);
      }
    } finally {
      setClaimsCheckLoader(false);
    }
  };

  useDocumentTitle("Waiting Room");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24" style={{ marginBottom: "0px" }}>
          <i className="docpro_blue_bg docpro_white_color">
            <Armchair width={20} height={20} />
          </i>
          Waiting room
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
        {can_visit('ehr_veradigm') && (
          <button
            className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
            onClick={() => {loadVeradigmData();}}
            disabled={activeVeradigmFetchData}
          >
            <RefreshCw width={20} height={20} />
            <span>Sync EHR Data</span>
            {activeVeradigmFetchData && (
              <span className="docpro_loading">
                <Loader width={20} height={20} />
              </span>
            )}
          </button>
        )}
      </div>
      <Spacing lg="5" sm="5" />

      <div className="docpro_data_table_wrap">
        <div className={`docpro_data_table_headings small_heading docpro_grid_gap_10 ${filter.start_date === filter.end_date ? "docpro_have_date_filter" : "" }`}>
          <div className={`searching_div docpro_grid_gap_10 ${(filter.start_date && (filter.start_date === filter.end_date) && (paginationCount.totalCount > 0) && specilityId) ? "docpro_sm_height_lg_42" : ""}`}>
            <form className="docpro_search_form">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            <div className={`flex items-center docpro_grid_gap_4 docpro_date_filter`}>
              <div className={`docpro_toggle_calendar ${filter.start_date ? "active" : "" }`}>
                <CalendarDays />

                {/* DatePicker range to single-date only */}
                {/*
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={handleDateChange}
                />
                */}
                <DatePicker
                  selected={dateRange[0]}
                  onChange={handleDateChange}
                />                
                {filter.start_date && filter.end_date && (
                  <>
                    <span>{exchangeDate(filter.start_date)}</span>
                    {filter.start_date !== filter.end_date && (
                      <span>{exchangeDate(filter.end_date)}</span>
                    )}
                  </>
                )}
              </div>
              {filter.start_date && (
                <span onClick={() => {
                  setFilter({
                    ...filter,
                    start_date: "",
                    end_date: "",
                    page: 1,
                  });
                  setDateRange([null, null]);
                  setDataLoading(true);
                }}>
                  <CircleX size={22} color="var(--accent)" className="pointer" />
                </span>
              )}
            </div>
          </div>

          <div className="docpro_data_table_btns p-0">
            {can_visit('eligibility') && (
              <button 
                className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                style={{ whiteSpace: 'nowrap', }}
                onClick={() => {
                  setBulkClaimsCheckModalOpen(!bulkClaimsCheckModalOpen);
                  setPreviousScrollPosition(window.scrollY);
                  window.scrollTo(0, 0);
                }}
                disabled = {bulkClaimsCheckLoader ? true : false}
              >
                <MonitorCheck width={20} height={20} />
                <span className="small_not_show">Check Claims</span>
                {bulkClaimsCheckLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            )}
            {filter.start_date && (paginationCount.totalCount > 0) && specilityId && (
              <div className={`${userProviders.length > 0 ? "docpro_bulk_buttons more_button" : "docpro_data_table_btns"}`}>
                {can_visit('_bulk_export') && (
                  <button 
                    className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                    style={{ whiteSpace: 'nowrap', }}
                    onClick={handleExportAll}
                    disabled = {exportAllButtonLoader ? true : false}
                  >
                    <FolderOutput width={20} height={20} />
                    <span className="small_not_show">Export All</span>
                    {exportAllButtonLoader && (
                      <span className="docpro_loading">
                        <Loader width={20} height={20} />
                      </span>
                    )}
                  </button>
                )}
                {can_visit("waiting_room_copy_codes") && (
                  <button 
                    type="button"
                    onClick={() => copyCodesData(filter)}
                    className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                    disabled={copyAllLoader ? true : false}
                  >
                    <Copy width={20} height={20} />
                    <span className="small_not_show">Copy All Codes</span>
                    {copyAllLoader && (
                      <span className="docpro_loading">
                        <Loader width={20} height={20} />
                      </span>
                    )}
                  </button>
                )}
                <button 
                  type="button"
                  onClick={() => downloadAllPrevisit(filter, null, selectedProvider)}
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  disabled={previsitButtonLoader ? true : false}
                >
                  <CloudDownload width={20} height={20} />
                  <span className="small_not_show">Download All Previsit</span>
                  {previsitButtonLoader && (
                    <span className="docpro_loading">
                      <Loader width={20} height={20} />
                    </span>
                  )}
                </button>
              </div>
            )}
            {userProviders.length > 0 && (
              role_has("Admin") ? (
                <select
                  className={`docpro_form_field docpro_waiting_select ${filter.start_date === filter.end_date ? "docpro_waiting_select_sm" : "docpro_waiting_select_lg"}`}
                  onChange={filterByProvider}
                  value={filter?.provider_id || ""}
                >
                  <option value="">All</option>
                  {userProviders?.map((provider, index) => (
                    <option key={index} value={provider.provider_id}>
                      {provider.last_name}{' '}{provider.first_name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="docpro_form_field docpro_waiting_select docpro_waiting_select_lg" style={{ minHeight: 42, display: "flex", alignItems: "center", fontWeight: 600 }}>
                  {selectedProvider}
                </div>
              )
            )}
          </div>
        </div>

        <WaitingRoomDataTable
          tableData={waitingRoomData}
          handleSelectAll={() => handleSelectAll(waitingRoomData)}
          handleCheckboxChange={handleCheckboxChange}
          handleDeleteRecord={handleDeleteRecord}
          handleSendReviewRequest={handleSendReviewRequest}
          messageTemplate={messageTemplate}
          handleSignOutSheet={handleSignOutSheet}
          downloadSignOutSheet={downloadSignOutSheet}
          reloadData={loadWaitingRoomData}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          setSmsLimit={setSmsLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          checkSelected={checkSelected}
          downloadBulkData={handleDownloadSelect}
          buttonLoader={buttonLoader}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          smsLimit={smsLimit}
          setLetterLimit={setLetterLimit}
          handleReferralNavigation={handleReferralNavigation}
          user_id={user_id}
          specilityId={specilityId}
          showPrevisitLoaderData={showPrevisitLoaderData}
          copyCodesData={copyCodesData}
          waitingRoomBulkDelete={waitingRoomBulkDelete}
          setWaitingRoomBulkDelete={setWaitingRoomBulkDelete}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
          selectedProvider={selectedProvider}
          checkBulkEligibility={checkBulkEligibility}
          eligibilityCheckLoader={eligibilityCheckLoader}
          checkBulkClaims={checkBulkClaims}
          claimsCheckLoader={claimsCheckLoader}
        />
        {(waitingRoomData.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {waitingRoomBulkDelete && (
        <BulkDeleteModal
          handleDelete={() => handleBulkDeleteRecord()}
          name={`${checkedIds?.length} records`}
          activeModal={waitingRoomBulkDelete}
          setActiveModal={setWaitingRoomBulkDelete}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {bulkClaimsCheckModalOpen && (
        <ClaimCheckImportModal
          account_id={account_id}
          location_id={location_id}
          activeModal={bulkClaimsCheckModalOpen}
          setActiveModal={setBulkClaimsCheckModalOpen}
          activeLoader={bulkClaimsCheckLoader}
          setActiveLoader={setBulkClaimsCheckLoader}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
